import { useState, useEffect } from "react";
import { voucherApi, accountApi } from "@/services/api";
import {
  Card,
  CardContent,
  CardDescription,
  CardTitle,
  CardHeader,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectValue,
  SelectTrigger,
  SelectItem,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import {
  PlusCircle,
  Edit,
  Trash2,
  Search,
  Calendar,
  CreditCard,
  DollarSign,
  Eye,
  MoreHorizontal,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import TablePagination from "@/components/masters/TablePagination";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/modal";
import { Textarea } from "@/components/ui/textarea";

// Define interfaces for API data
interface Account {
  id: string;
  name: string;
  description?: string;
  type: "cash" | "bank";
  visibility?: "payment" | "receipt" | "both";
}

interface Voucher {
  id: string;
  entryNumber: string;
  voucherNumber: string;
  description: string;
  fromAccountId: string;
  fromAccountName?: string;
  fromAccountDescription?: string;
  toAccountId: string;
  toAccountName?: string;
  toAccountDescription?: string;
  amount: number;
  date: string;
  type: "cash" | "bank";
  voucherType: "payment" | "receipt";
}

interface VoucherPageProps {
  isReceipt?: boolean;
}

// Type for voucher fetch params
interface VoucherFetchParams {
  page: number;
  limit: number;
  voucherType: string;
  query?: string;
  type?: string;
  fromAccountId?: string;
  toAccountId?: string;
  fromDate?: string;
  toDate?: string;
}

const VoucherPage = ({ isReceipt }: VoucherPageProps) => {
  // State for vouchers and accounts data
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalDocs, setTotalDocs] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);

  // UI state
  const [showVoucherForm, setShowVoucherForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
    fromAccountId: "all",
    toAccountId: "all",
    type: "all",
    query: "",
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingVoucherId, setEditingVoucherId] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [voucherToDelete, setVoucherToDelete] = useState<string | null>(null);
  const [paymentType, setPaymentType] = useState<"cash" | "bank">("cash");

  // Default form data
  const defaultFormData = {
    entryNumber: "",
    voucherNumber: "",
    description: "",
    fromAccountId: "",
    fromAccountDescription: "",
    toAccountId: "",
    toAccountDescription: "",
    amount: 0,
    date: new Date().toISOString().split("T")[0],
    type: "cash" as "cash" | "bank",
    voucherType: isReceipt ? "receipt" : "payment",
  };

  const [formData, setFormData] = useState(defaultFormData);
  console.log(formData, "FORMDATA");

  const { toast } = useToast();

  type FormField = keyof typeof defaultFormData;
  type FormValue = string | number | Date | ("cash" | "bank");

  // Add state for account validation error
  const [accountError, setAccountError] = useState("");

  // State for from and to accounts
  const [fromAccounts, setFromAccounts] = useState<Account[]>([]);
  const [toAccounts, setToAccounts] = useState<Account[]>([]);
  // Separate state for filter's From Account dropdown
  const [filterFromAccounts, setFilterFromAccounts] = useState<Account[]>([]);

  // Fetch vouchers from API
  useEffect(() => {
    fetchVouchers();
  }, [currentPage, itemsPerPage, isReceipt, filters]);

  // Fetch accounts from API
  useEffect(() => {
    console.log("Fetching accounts due to payment type or receipt type change");
    fetchAccounts();
  }, [paymentType, isReceipt]);

  // Fetch accounts when form is opened
  useEffect(() => {
    if (showVoucherForm) {
      console.log("Fetching accounts because form was opened");
      fetchAccounts();
    }
  }, [showVoucherForm]);

  // Fetch all accounts for filter's From Account dropdown (not filtered by paymentType)
  useEffect(() => {
    const fetchFilterFromAccounts = async () => {
      try {
        // Fetch all accounts with visibility for this voucher type
        const visibility = isReceipt ? "receipt" : "payment";
        const response = await accountApi.getAccounts({ visibility });
        if (response && response.docs) {
          setFilterFromAccounts(response.docs);
        }
      } catch (error) {
        toast({
          title: "Error",
          description:
            error.message ??
            "Failed to fetch filter accounts. Please try again.",
          variant: "destructive",
        });
      }
    };
    fetchFilterFromAccounts();
  }, [isReceipt]);

  // Function to fetch vouchers
  const fetchVouchers = async () => {
    try {
      setLoading(true);
      const voucherType = isReceipt ? "receipt" : "payment";

      // Prepare filter parameters
      const params: VoucherFetchParams = {
        page: currentPage,
        limit: itemsPerPage,
        voucherType: voucherType,
      };

      // Add optional filters if they exist
      if (filters.query) params.query = filters.query.trim();
      if (filters.type !== "all") params.type = filters.type;
      if (filters.fromAccountId && filters.fromAccountId !== "all")
        params.fromAccountId = filters.fromAccountId;
      if (filters.toAccountId && filters.toAccountId !== "all")
        params.toAccountId = filters.toAccountId;
      if (filters.fromDate) params.fromDate = filters.fromDate;
      if (filters.toDate) params.toDate = filters.toDate;

      console.log("Fetching vouchers with params:", params);
      const response = await voucherApi.getVouchers(params);

      if (response && response.docs) {
        setVouchers(response.docs);
        setTotalDocs(response.totalDocs);
        setHasNextPage(response.hasNextPage);
      }
    } catch (error) {
      toast({
        title: "Error",
        description:
          error.message ?? "Failed to fetch vouchers. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch accounts
  const fetchAccounts = async () => {
    try {
      const visibility = isReceipt ? "receipt" : "payment";

      // Fetch from accounts filtered by both type and visibility
      console.log(
        "Fetching FROM accounts for type:",
        paymentType,
        "and visibility:",
        visibility
      );
      const fromAccountResponse =
        await accountApi.getAccountsByTypeAndVisibility(
          paymentType,
          visibility
        );
      console.log("FROM Accounts response:", fromAccountResponse);

      if (fromAccountResponse && fromAccountResponse.docs) {
        setFromAccounts(fromAccountResponse.docs);
      }

      // Fetch to accounts filtered only by visibility
      console.log("Fetching TO accounts for visibility:", visibility);
      const toAccountResponse = await accountApi.getAccounts({
        visibility: visibility,
        status: "true",
      });
      console.log("TO Accounts response:", toAccountResponse);

      if (toAccountResponse && toAccountResponse.docs) {
        setToAccounts(toAccountResponse.docs);
      }

      // Set the combined accounts for backward compatibility
      const allAccounts = [
        ...(fromAccountResponse?.docs || []),
        ...(toAccountResponse?.docs || []),
      ];
      // Remove duplicates based on account ID
      const uniqueAccounts = allAccounts.filter(
        (account, index, self) =>
          index === self.findIndex((a) => a.id === account.id)
      );
      setAccounts(uniqueAccounts);
    } catch (error) {
      toast({
        title: "Error",
        description:
          error.message ?? "Failed to fetch accounts. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Function to fetch entry number for new voucher
  const fetchEntryNumber = async () => {
    try {
      const voucherType = isReceipt ? "receipt" : "payment";
      const response = await voucherApi.getEntryNumber(voucherType);
      if (response && response.data) {
        return response.data.trim();
      }
      return "";
    } catch (error) {
      return "";
    }
  };

  // Function to handle filter changes
  const handleFilterChange = (
    filterName: keyof typeof filters,
    value: string
  ) => {
    setFilters({
      ...filters,
      [filterName]: value,
    });

    // Reset to first page when filters change
    setCurrentPage(1);

    // If changing payment method, also update form data
    if (filterName === "type" && (value === "cash" || value === "bank")) {
      setPaymentType(value as "cash" | "bank");
    }
  };

  // Reset all filters
  const resetFilters = () => {
    setFilters({
      fromDate: "",
      toDate: "",
      fromAccountId: "all",
      toAccountId: "all",
      type: "all",
      query: "",
    });
    setCurrentPage(1);
    fetchVouchers();
  };

  // Apply filters and fetch vouchers
  const applyFilters = () => {
    // Reset to first page when applying filters
    setCurrentPage(1);
    fetchVouchers();
  };

  // Function to get account name by ID
  const getAccountName = (accountId: string) => {
    const account = accounts.find((acc) => acc.id === accountId);
    return account ? account.name : "Unknown Account";
  };

  // Function to get account description by ID
  const getAccountDescription = (accountId: string) => {
    const account = accounts.find((acc) => acc.id === accountId);
    return account ? account.description?.trim() || "" : "";
  };

  // Handle form input change
  const handleInputChange = async (field: FormField, value: FormValue) => {
    const updatedFormData = { ...formData, [field]: value };

    // Update account descriptions when accounts change
    if (field === "fromAccountId" && typeof value === "string") {
      updatedFormData.fromAccountDescription = getAccountDescription(value);
    } else if (field === "toAccountId" && typeof value === "string") {
      updatedFormData.toAccountDescription = getAccountDescription(value);
    } else if (field === "type") {
      setPaymentType(value as "cash" | "bank");
      updatedFormData.fromAccountId = "";
      updatedFormData.toAccountId = "";
      updatedFormData.fromAccountDescription = "";
      updatedFormData.toAccountDescription = "";
      setFormData(updatedFormData);
      try {
        await fetchAccounts();
        return;
      } catch (error) {
        console.error(
          "Error fetching accounts after payment type change:",
          error
        );
      }
    }

    // Validation: from and to account must not be the same
    if (
      field === "fromAccountId" ||
      field === "toAccountId" ||
      (field === "type" &&
        updatedFormData.fromAccountId &&
        updatedFormData.toAccountId)
    ) {
      if (
        updatedFormData.fromAccountId &&
        updatedFormData.toAccountId &&
        updatedFormData.fromAccountId === updatedFormData.toAccountId
      ) {
        setAccountError("From and To accounts cannot be the same.");
      } else {
        setAccountError("");
      }
    }

    setFormData(updatedFormData);
  };

  // Handle edit voucher
  const handleEdit = async (voucher: Voucher) => {
    setIsEditMode(true);
    setEditingVoucherId(voucher.id);

    try {
      const response = await voucherApi.getVoucher(voucher.id);
      console.log("Voucher details response:", response);

      if (response && response.data) {
        const voucherData = response.data;

        // First update payment type for account filtering
        setPaymentType(voucherData.type as "cash" | "bank");

        // Then fetch accounts for this voucher type and payment method
        const visibility = isReceipt ? "receipt" : "payment";
        const accountResponse = await accountApi.getAccountsByTypeAndVisibility(
          voucherData.type,
          visibility
        );
        console.log("Accounts response:", accountResponse);

        if (accountResponse && accountResponse.docs) {
          // Set accounts directly to ensure they're available before setting form data
          setAccounts(accountResponse.docs);

          // Set form data after accounts are loaded
          setFormData({
            entryNumber: voucherData.entryNumber.trim(),
            voucherNumber: voucherData.voucherNumber.trim(),
            description: voucherData.description.trim(),
            fromAccountId:
              voucherData.fromAccount?.id || voucherData.fromAccountId,
            fromAccountDescription:
              voucherData.fromAccountDescription?.trim() || "",
            toAccountId: voucherData.toAccount?.id || voucherData.toAccountId,
            toAccountDescription:
              voucherData.toAccountDescription?.trim() || "",
            amount: voucherData.amount,
            date: new Date(voucherData.date).toISOString().split("T")[0],
            type: voucherData.type as "cash" | "bank",
            voucherType: voucherData.voucherType,
          });
        }
      }
    } catch (error) {
      toast({
        title: "Error",
        description:
          error.message ?? "Failed to fetch voucher details. Please try again.",
        variant: "destructive",
      });
    }

    setShowVoucherForm(true);
  };

  // Handle delete voucher
  const handleDelete = (voucherId: string) => {
    setVoucherToDelete(voucherId);
    setDeleteDialogOpen(true);
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (voucherToDelete) {
      try {
        await voucherApi.deleteVoucher(voucherToDelete);
        setDeleteDialogOpen(false);
        setVoucherToDelete(null);

        toast({
          title: "Success",
          description: "Voucher deleted successfully.",
        });

        // Refresh vouchers list
        fetchVouchers();
      } catch (error) {
        toast({
          title: "Error",
          description:
            error.message ?? "Failed to delete voucher. Please try again.",
          variant: "destructive",
        });
      }
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      formData.fromAccountId === formData.toAccountId &&
      formData.fromAccountId &&
      formData.toAccountId
    ) {
      setAccountError("From and To accounts cannot be the same.");
      return;
    }
    if (formData.amount <= 0) {
      setAccountError("Amount must be greater than zero.");
      return;
    }
    if (accountError === "Amount must be greater than zero.") {
      setAccountError("");
    }

    try {
      // Trim all string fields before submission
      const trimmedData = {
        ...formData,
        entryNumber: formData.entryNumber.trim(),
        voucherNumber: formData.voucherNumber.trim(),
        description: formData.description.trim(),
        fromAccountDescription: formData.fromAccountDescription?.trim() || "",
        toAccountDescription: formData.toAccountDescription?.trim() || "",
      };

      if (isEditMode && editingVoucherId) {
        // Update existing voucher
        await voucherApi.updateVoucher(editingVoucherId, trimmedData);
        toast({
          title: "Voucher Updated",
          description: "The voucher has been updated successfully.",
        });
      } else {
        // Create new voucher
        const entryNumber = await fetchEntryNumber();
        const newVoucherData = {
          ...trimmedData,
          entryNumber: entryNumber || trimmedData.entryNumber,
          voucherType: isReceipt ? "receipt" : "payment",
        };

        await voucherApi.createVoucher(newVoucherData);
        toast({
          title: "Voucher Created",
          description: "The new voucher has been created successfully.",
        });
      }

      // Reset form and close dialog
      setFormData(defaultFormData);
      setShowVoucherForm(false);
      setIsEditMode(false);
      setEditingVoucherId(null);

      // Refresh vouchers list
      fetchVouchers();
    } catch (error) {
      toast({
        title: "Error",
        description:
          error.message ?? "Failed to save voucher. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Cancel form
  const cancelForm = () => {
    setShowVoucherForm(false);
    setFormData(defaultFormData);
    setIsEditMode(false);
    setEditingVoucherId(null);
  };

  // Add view modal for voucher details
  const [viewVoucher, setViewVoucher] = useState<Voucher | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [isViewLoading, setIsViewLoading] = useState(false);

  const handleView = async (voucher: Voucher) => {
    setIsViewLoading(true);
    setShowViewModal(true);
    try {
      const response = await voucherApi.getVoucher(voucher.id);
      setViewVoucher(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message ?? "Failed to load voucher details",
        variant: "destructive",
      });
      setShowViewModal(false);
    } finally {
      setIsViewLoading(false);
    }
  };

  // Helper function to format date to 'dd-mm-yyyy'
  const formatDateISO = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {isReceipt ? "Receipt Vouchers" : "Payment Vouchers"}
          </h1>
          <p className="text-muted-foreground">
            {isReceipt
              ? "Create and manage receipt vouchers"
              : "Create and manage payment vouchers"}
          </p>
        </div>
        <Button
          onClick={async () => {
            // Reset form state
            setIsEditMode(false);
            setEditingVoucherId(null);

            try {
              // Get new entry number
              const entryNumber = await fetchEntryNumber();

              // Set form data with default values
              setFormData({
                ...defaultFormData,
                entryNumber: entryNumber || "",
                date: new Date().toISOString().split("T")[0],
                type: paymentType,
              });

              // Fetch accounts for the default paymentType (usually 'cash')
              await fetchAccounts();

              // Show the form after preparation
              setShowVoucherForm(true);
            } catch (error) {
              toast({
                title: "Error",
                description:
                  error.message ??
                  "Failed to prepare voucher form. Please try again.",
                variant: "destructive",
              });
            }
          }}
        >
          <PlusCircle className="mr-2 h-4 w-4" />
          Create {isReceipt ? "Receipt" : "Payment"}
        </Button>
      </div>

      {/* Filters Section */}
      <div className="mb-4">
        {/* Search bar row for large screens */}
        <div className="hidden sm:flex mb-2 gap-4">
          <div className="flex-1">
            <div className="space-y-1">
              <Label className="text-xs invisible">Search</Label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by entry/voucher or description"
                  value={filters.query}
                  onChange={(e) => handleFilterChange("query", e.target.value)}
                  className="pl-8 w-full h-10 placeholder:text-m"
                />
              </div>
            </div>
          </div>
        </div>
        {/* Responsive filter grid for all screens */}
        <div
          className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-x-6 md:gap-y-4 lg:grid-cols-6 lg:gap-4 items-end"
        >
          {/* On small screens, show search bar here */}
          <div className="flex-1 sm:hidden">
            <div className="space-y-1">
              <Label className="text-xs invisible">Search</Label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by entry/voucher or description"
                  value={filters.query}
                  onChange={(e) => handleFilterChange("query", e.target.value)}
                  className="pl-8 w-full h-10 placeholder:text-xs"
                />
              </div>
            </div>
          </div>
          <div className="space-y-1 w-full">
            <Label htmlFor="fromDate" className="text-xs">
              From Date
            </Label>
            <Input
              id="fromDate"
              type="date"
              value={filters.fromDate}
              onChange={(e) => handleFilterChange("fromDate", e.target.value)}
              className="w-full h-10"
            />
          </div>
          <div className="space-y-1 w-full">
            <Label htmlFor="toDate" className="text-xs">
              To Date
            </Label>
            <Input
              placeholder="To Date"
              id="toDate"
              type="date"
              value={filters.toDate}
              min={filters.fromDate || undefined}
              onChange={(e) => handleFilterChange("toDate", e.target.value)}
              className="w-full h-10"
            />
          </div>
          <div className="w-full">
            <Select
              value={filters.fromAccountId}
              onValueChange={(value) =>
                handleFilterChange("fromAccountId", value)
              }
            >
              <SelectTrigger className="w-full h-10">
                <SelectValue placeholder="From Account" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All From Accounts</SelectItem>
                {filterFromAccounts.map((account) => (
                  <SelectItem key={account.id} value={account.id}>
                    {account.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-full">
            <Select
              value={filters.toAccountId}
              onValueChange={(value) =>
                handleFilterChange("toAccountId", value)
              }
            >
              <SelectTrigger className="w-full h-10">
                <SelectValue placeholder="To Account" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All To Accounts</SelectItem>
                {toAccounts.map((account) => (
                  <SelectItem key={account.id} value={account.id}>
                    {account.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {/* Reset button: right-aligned on md+/grid, full-width on mobile */}
          <div className="w-full flex md:col-span-2 lg:col-span-1 justify-end">
            <Button
              variant="outline"
              onClick={resetFilters}
              className="w-full md:w-auto h-10"
            >
              Reset
            </Button>
          </div>
        </div>
      </div>

      {showVoucherForm && (
        <Dialog open={showVoucherForm} onOpenChange={setShowVoucherForm}>
          <DialogContent size="lg">
            <DialogHeader>
              <div className="flex items-start justify-between w-full">
                <div>
                  <DialogTitle>
                    {isEditMode
                      ? `Edit ${isReceipt ? "Receipt" : "Payment"}`
                      : `Create New ${isReceipt ? "Receipt" : "Payment"}`}
                  </DialogTitle>
                  <DialogDescription>
                    {isEditMode
                      ? "Update voucher information"
                      : `Enter the details for the new ${
                          isReceipt ? "receipt" : "payment"
                        } voucher`}
                  </DialogDescription>
                </div>
                <div className="flex items-center gap-2 bg-muted p-1 rounded-lg">
                  <Button
                    variant={formData.type === "cash" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => handleInputChange("type", "cash")}
                    className="px-3"
                  >
                    {/* <DollarSign className="h-4 w-4 mr-2" /> */}
                    Cash
                  </Button>
                  <Button
                    variant={formData.type === "bank" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => handleInputChange("type", "bank")}
                    className="px-3"
                  >
                    <CreditCard className="h-4 w-4 mr-2" />
                    Bank
                  </Button>
                </div>
              </div>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-6 py-4">
                {/* First row: Entry No and Date */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="entryNo">Entry Number</Label>
                    <Input
                      id="entryNo"
                      value={formData.entryNumber}
                      placeholder="V0001"
                      disabled={true}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="date">Date</Label>
                    <Input
                      id="date"
                      type="date"
                      value={formData.date}
                      disabled={true}
                      required
                    />
                  </div>
                </div>

                {/* Second row: Voucher Number and Description */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="voucherNumber">
                      Voucher Number <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="voucherNumber"
                      value={formData.voucherNumber}
                      onChange={(e) =>
                        handleInputChange("voucherNumber", e.target.value)
                      }
                      placeholder="Enter voucher number"
                      className="w-full placeholder:text-base sm:placeholder:text-base placeholder:text-s"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="description">
                      Description <span className="text-red-500">*</span>
                    </Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) =>
                        handleInputChange(
                          "description",
                          e.target.value.trimStart()
                        )
                      }
                      placeholder="Enter voucher description"
                      className="h-20"
                      required
                    />
                  </div>
                </div>

                {/* Third row: From Account and To Account */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="fromAccount">
                      From Account <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={formData.fromAccountId}
                      onValueChange={(value) => {
                        console.log("From Account selected:", value);
                        handleInputChange("fromAccountId", value);
                      }}
                      required
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select account">
                          {fromAccounts.find(
                            (acc) => acc.id === formData.fromAccountId
                          )?.name || "Select account"}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {fromAccounts.map((account) => (
                          <SelectItem key={account.id} value={account.id}>
                            {account.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="toAccount">
                      To Account <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={formData.toAccountId}
                      onValueChange={(value) => {
                        console.log("To Account selected:", value);
                        handleInputChange("toAccountId", value);
                      }}
                      required
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select account">
                          {toAccounts.find(
                            (acc) => acc.id === formData.toAccountId
                          )?.name || "Select account"}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {toAccounts.map((account) => (
                          <SelectItem key={account.id} value={account.id}>
                            {account.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Error message below both dropdowns, spanning both columns */}
                  {accountError &&
                    accountError !== "Amount must be greater than zero." && (
                      <div className="col-span-2 text-red-500 text-sm mt-1">
                        {accountError}
                      </div>
                    )}
                </div>

                {/* Account Descriptions */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={formData.fromAccountDescription}
                      onChange={(e) =>
                        handleInputChange(
                          "fromAccountDescription",
                          e.target.value.trimStart()
                        )
                      }
                      placeholder="Enter description for from account"
                      className="h-20"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={formData.toAccountDescription}
                      onChange={(e) =>
                        handleInputChange(
                          "toAccountDescription",
                          e.target.value.trimStart()
                        )
                      }
                      placeholder="Enter description for to account"
                      className="h-20"
                    />
                  </div>
                </div>

                {/* Amount */}
                <div className="space-y-2">
                  <Label htmlFor="amount">
                    Amount <span className="text-red-500">*</span>
                  </Label>
                  <div className="flex items-center gap-2">
                    {/* <DollarSign className="h-4 w-4 text-muted-foreground" /> */}
                    <Input
                      id="amount"
                      type="number"
                      pattern="^\\d{1,10}(\\.\\d{1,2})?$"
                      value={
                        Number.isFinite(formData.amount)
                          ? formData.amount.toLocaleString("en-US", {
                              useGrouping: false,
                              maximumFractionDigits: 2,
                              minimumFractionDigits: 0,
                            })
                          : ""
                      }
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^\d.]/g, "");
                        const parts = val.split(".");
                        const intPart = parts[0].slice(0, 10); // up to 10 digits before decimal
                        const decPart = parts[1] ? parts[1].slice(0, 2) : "";
                        const newVal =
                          decPart !== "" ? `${intPart}.${decPart}` : intPart;
                        let num = parseFloat(newVal) || 0;
                        if (num > 9999999999.99) num = 9999999999.99;
                        handleInputChange("amount", num);
                      }}
                      placeholder="Enter amount"
                      inputMode="decimal"
                      maxLength={13}
                      style={{
                        overflowX: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "100%",
                        MozAppearance: "textfield",
                        appearance: "textfield",
                      }}
                      required
                    />
                  </div>
                  {accountError === "Amount must be greater than zero." && (
                    <div className="text-red-500 text-sm mt-1">
                      {accountError}
                    </div>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" type="button" onClick={cancelForm}>
                  Cancel
                </Button>
                <Button type="submit">
                  {isEditMode ? "Update Voucher" : "Create Voucher"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}

      <Card>
        <CardHeader className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
          {/* <div>
            <CardTitle>{isReceipt ? "Receipt" : "Payment"} Vouchers</CardTitle>
            <CardDescription>
              View and manage all {isReceipt ? "receipt" : "payment"} vouchers
            </CardDescription>
          </div> */}
          {/* <div className="text-sm text-muted-foreground">
            Showing {vouchers.length} of {vouchers.length} vouchers
          </div> */}
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="h-10">
                <TableHead className="whitespace-nowrap px-3 py-2">Entry No</TableHead>
                <TableHead className="whitespace-nowrap px-3 py-2">Voucher Number</TableHead>
                <TableHead className="whitespace-nowrap px-3 py-2">Date</TableHead>
                <TableHead className="hidden lg:table-cell whitespace-nowrap px-3 py-2">
                  Description
                </TableHead>
                <TableHead className="whitespace-nowrap px-3 py-2">From Account</TableHead>
                <TableHead className="whitespace-nowrap px-3 py-2">To Account</TableHead>
                <TableHead className="whitespace-nowrap px-3 py-2">Amount</TableHead>
                <TableHead className="w-[50px] px-3 py-2">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {vouchers.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="text-center px-3 py-2 text-muted-foreground"
                  >
                    {filters.query
                      ? "No vouchers match your search"
                      : "No vouchers found. Create one to get started."}
                  </TableCell>
                </TableRow>
              ) : (
                vouchers.map((voucher) => (
                  <TableRow key={voucher.id} className="h-10">
                    <TableCell className="font-medium">
                      {voucher.entryNumber}
                    </TableCell>
                    <TableCell>{voucher.voucherNumber}</TableCell>
                    <TableCell>{formatDateISO(voucher.date)}</TableCell>
                    <TableCell className="hidden lg:table-cell max-w-[200px] truncate">
                      {voucher.description}
                    </TableCell>
                    <TableCell>
                      {getAccountName(voucher.fromAccountId || "")}
                    </TableCell>
                    <TableCell>
                      {getAccountName(voucher.toAccountId || "")}
                    </TableCell>
                    <TableCell>{voucher.amount.toLocaleString()}</TableCell>
                    <TableCell className="px-3 py-2">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 p-0">
                            <MoreHorizontal className="h-4 w-4" />
                            <span className="sr-only">Open menu</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleView(voucher)} className="cursor-pointer">
                            <Eye className="h-4 w-4 mr-2" />
                            View
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleEdit(voucher)} className="cursor-pointer">
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDelete(voucher.id)} 
                            className="cursor-pointer text-destructive focus:text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-2">
            <TablePagination
              currentPage={currentPage}
              totalPages={Math.ceil(totalDocs / itemsPerPage)}
              onPageChange={setCurrentPage}
            />
          </div>
        </CardContent>
      </Card>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>Are you sure?</DialogTitle>
            <DialogDescription>
              This action cannot be undone. This will permanently delete the
              voucher and all associated data.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Voucher Modal */}
      {showViewModal && viewVoucher && (
        <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Voucher Details</DialogTitle>
              <DialogDescription>
                View detailed information about the voucher
              </DialogDescription>
            </DialogHeader>
            {isViewLoading ? (
              <div className="flex justify-center items-center py-10">
                <span>Loading...</span>
              </div>
            ) : (
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Entry Number</Label>
                    <p className="text-sm">{viewVoucher.entryNumber}</p>
                  </div>
                  <div>
                    <Label>Voucher Number</Label>
                    <p className="text-sm">{viewVoucher.voucherNumber}</p>
                  </div>
                  <div>
                    <Label>Date</Label>
                    <p className="text-sm">{formatDateISO(viewVoucher.date)}</p>
                  </div>
                  <div>
                    <Label>Type</Label>
                    <p className="text-sm">{viewVoucher.type}</p>
                  </div>
                  <div>
                    <Label>Voucher Type</Label>
                    <p className="text-sm">{viewVoucher.voucherType}</p>
                  </div>
                  <div>
                    <Label>Description</Label>
                    <p className="text-sm" style={{ whiteSpace: "pre-line" }}>
                      {viewVoucher.description}
                    </p>
                  </div>
                  <div>
                    <Label>From Account</Label>
                    <p className="text-sm">
                      {getAccountName(viewVoucher.fromAccountId)}
                    </p>
                  </div>
                  <div>
                    <Label>To Account</Label>
                    <p className="text-sm">
                      {getAccountName(viewVoucher.toAccountId)}
                    </p>
                  </div>
                  <div>
                    <Label>From Account Description</Label>
                    <p className="text-sm" style={{ whiteSpace: "pre-line" }}>
                      {viewVoucher.fromAccountDescription}
                    </p>
                  </div>
                  <div>
                    <Label>To Account Description</Label>
                    <p className="text-sm" style={{ whiteSpace: "pre-line" }}>
                      {viewVoucher.toAccountDescription}
                    </p>
                  </div>
                  <div>
                    <Label>Amount</Label>
                    <p className="text-sm">
                      {viewVoucher.amount.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowViewModal(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default VoucherPage;
